import {
    Typography,
    Box,
    Paper,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useState, useEffect } from 'react';
import { paymentsApi, CaProduct, CaPaymentMethod } from '../services/payments-api.service';
import { useAuth } from '../contexts/AuthContext';
import { Action, canPerformAction } from '../services/permissions.service';

interface ProductListProps {
    onEditProduct?: (product: CaProduct) => void;
    refreshTrigger?: number;
}

export function ProductList({ onEditProduct, refreshTrigger }: ProductListProps) {
    const [products, setProducts] = useState<CaProduct[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { role } = useAuth();
    const canEditProducts = canPerformAction(role, Action.editProducts);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await paymentsApi.getProducts();
            if (response.errors && response.errors.length > 0) {
                setError(response.errors.join(', '));
            }
            setProducts(response.products || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            fetchProducts();
        }
    }, [refreshTrigger]);

    const handleEditProduct = (product: CaProduct) => {
        onEditProduct?.(product);
    };

    const handleRowDoubleClick = (product: CaProduct) => {
        if (canEditProducts) {
            handleEditProduct(product);
        }
    };

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading...' : `${products.length} products loaded`}
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 700 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ py: 1 }}>Product SKU</TableCell>
                                <TableCell sx={{ py: 1 }}>MFI Event Type</TableCell>
                                <TableCell sx={{ py: 1 }}>Tax Category</TableCell>
                                <TableCell sx={{ py: 1 }}>Payment Methods</TableCell>
                                <TableCell sx={{ py: 1 }}># Payments</TableCell>
                                <TableCell sx={{ py: 1 }}>Admin Fee</TableCell>
                                <TableCell sx={{ py: 1 }}>Name (English)</TableCell>
                                <TableCell sx={{ py: 1 }}>Nom (Français)</TableCell>
                                <TableCell sx={{ py: 1 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {products.map((product) => (
                                <TableRow
                                    key={`${product.productSku}-${product.eventType}`}
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '&:hover': { backgroundColor: 'action.hover' },
                                        cursor: canEditProducts ? 'pointer' : 'default',
                                        userSelect: 'none',
                                    }}
                                    onDoubleClick={() => handleRowDoubleClick(product)}
                                >
                                    <TableCell>{product.productSku}</TableCell>
                                    <TableCell>{product.eventType}</TableCell>
                                    <TableCell>
                                        {product.taxCategory === 'undefined' ? '--' : product.taxCategory}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 10 }}>
                                        {product.paymentMethods && product.paymentMethods.length > 0
                                            ? product.paymentMethods
                                                  .map((m) => {
                                                      switch (m) {
                                                          case CaPaymentMethod.creditCard:
                                                              return 'Credit Card';
                                                          case CaPaymentMethod.eTransfer:
                                                              return 'eTransfer';
                                                          case CaPaymentMethod.account:
                                                              return 'Account';
                                                          default:
                                                              return m;
                                                      }
                                                  })
                                                  .join(', ')
                                            : '--'}
                                    </TableCell>
                                    <TableCell>
                                        {product.numberOfPayments > 1 ? product.numberOfPayments : '--'}
                                    </TableCell>
                                    <TableCell>
                                        {product.adminFee > 0 ? `$${(product.adminFee / 100).toFixed(2)}` : '--'}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {product.translations?.en?.name || '-'}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {product.translations?.fr?.name || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {canEditProducts && (
                                            <Tooltip title="Edit Product">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditProduct(product)}
                                                    color="primary"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Loading products...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && products.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography color="text.secondary">No products found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </>
    );
}
